'use client';

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import Typography from '../Typography/Typography';
import Iconography from '../Iconography/Iconography';
import styles from './Button.module.scss';

type ButtonVariant = 'button' | 'link';
type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'text';
type ButtonSize = 'large' | 'medium' | 'small';
type Theme = 'dark' | 'light';
type Color =
  | 'brand'
  | 'neutral'
  | 'sapphire'
  | 'error-1'
  | 'error-2'
  | 'information-1'
  | 'information-2'
  | 'success-1'
  | 'success-2'
  | 'warning-1'
  | 'warning-2';
type Direction = 'ltr' | 'rtl';

interface IconProps {
  category: string;
  name: string;
  dataTestId?: string;
}

export interface ButtonProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  variant?: ButtonVariant;
  label?: string;
  type?: ButtonType;
  size?: ButtonSize;
  iconLeft?: IconProps;
  iconRight?: IconProps;
  href?: string;
  target?: string;
  gaComponent: string;
  gaModel?: string;
  linkContext?: string;
  dataTestId: string;
  handleClick?: (event: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  isFluid?: boolean;
  disabled?: boolean;
  theme?: Theme;
  position?: string | null;
  interactionType?: string;
  direction?: Direction;
  color?: Color;
  noPadding?: boolean;
  showSkeleton?: boolean;
  uiId?: string;
  logEvents?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'button',
  label,
  type = 'primary',
  size = 'medium',
  iconLeft,
  iconRight,
  href,
  target,
  gaComponent,
  gaModel,
  linkContext,
  dataTestId,
  handleClick,
  className,
  isFluid = false,
  disabled = false,
  theme = 'dark',
  position = null,
  interactionType,
  direction = 'ltr',
  color = 'brand',
  noPadding = false,
  showSkeleton = false,
  uiId,
  logEvents = true,
  ...props
}) => {
  const [localizedHref, setLocalizedHref] = useState(href);

  useEffect(() => {
    setLocalizedHref(href);
  }, [href]);

  if (showSkeleton) {
    return <div className={classnames(styles.button, styles[size])} />;
  }

  let HtmlTag: React.ElementType = 'button';
  let tagAttributes: Record<string, string | undefined> = {};

  if (variant === 'link') {
    HtmlTag = 'a';
    tagAttributes = { href: localizedHref, target };
  }

  const renderIcon = (icon: IconProps | undefined, position: 'left' | 'right') => {
    if (!icon) return null;

    type IconCategory =
      | 'global'
      | 'arrows'
      | 'hardware'
      | 'media'
      | 'map'
      | 'features'
      | 'social'
      | 'brand'
      | 'privacy';

    return (
      <span
        className={classnames({
          [styles[`iconLeft_${direction}`]]: position === 'left',
          [styles[`iconRight_${direction}`]]: position === 'right',
        })}
        data-testid={icon.dataTestId}
      >
        <Iconography
          category={icon.category as IconCategory}
          name={icon.name}
          theme={theme}
          color={type === 'text' ? 'primary-1' : 'white'}
          size={{ xs: 16, md: 20 }}
        />
      </span>
    );
  };

  const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
    // Prevent execution if button is disabled
    if (disabled) {
      e.preventDefault();
      return;
    }

    if (typeof window !== `undefined` && logEvents) {
      const dataLayerMap: Record<string, string | undefined> = {
        event: 'interaction',
        interaction__type: interactionType
          ? interactionType
          : type === 'text'
            ? 'hyperlink'
            : 'cta',
        interaction__component: gaComponent,
      };

      if (variant === 'link') {
        dataLayerMap.interaction__destination = href;
      }

      label && (dataLayerMap.interaction__text = label);
      linkContext && (dataLayerMap.interaction__linkContext = linkContext);
      position && (dataLayerMap.position = position);
      gaModel && (dataLayerMap.model = gaModel);

      // Add to dataLayer if available
      if ('dataLayer' in window) {
        (
          window as unknown as { dataLayer: Array<Record<string, string | undefined>> }
        ).dataLayer.push(dataLayerMap);
      }
    }

    if (uiId) {
      return;
    }

    if (handleClick) {
      handleClick(e);
    }
  };

  return (
    <HtmlTag
      className={classnames(
        styles.button,
        styles[size],
        styles[`${theme}_${color}_${type}`],
        className,
        {
          [styles.isFluid]: isFluid,
          [styles.disabled]: disabled,
          [styles.noPadding]: noPadding,
        }
      )}
      onClick={handleOnClick}
      disabled={variant === 'button' ? disabled : undefined}
      aria-disabled={disabled}
      {...tagAttributes}
      data-testid={dataTestId}
      data-uiid={uiId}
      {...props}
    >
      {renderIcon(iconLeft, 'left')}
      {label && (
        <Typography variant="button" size={size} align="center" direction={direction}>
          {label}
        </Typography>
      )}
      {renderIcon(iconRight, 'right')}
    </HtmlTag>
  );
};

Button.displayName = 'Button';

export default Button;
